import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Block } from './Blocks/Block'
import { AnswersContext } from './context'
import { Button, Skeleton } from 'antd'
import {
  BizTypeEnum,
  PaperAnswer,
  PaperAnswerReq,
  PaperBlock,
  PaperReview,
  courseApi,
  paperApi,
  quizApi,
} from 'packages/sdks-next/growp'
import { Toast } from '@seiue/ui'
import { CreateReviewParams, CreateReviewPayload, QuizMode } from './types'
import { AddSerialNumberForQuestionBlock } from './utils'

/**
 * 试卷 - 预览态|作答态|等待批阅态|批阅态|已批阅态
 * @param param - 参数
 * @param param.loading - loading状态,用于骨架屏显示与隐藏
 * @param param.mode - 模式
 * @param param.showScore - 是否显示分数
 * @param param.blocks - 题目块
 * @param param.initValue - 用户已经提交的答案详情
 * @param param.reviews - 批阅详情
 * @param param.isForeground - 是否是前台页面
 * @param param.onSubmit - 提交答案回调
 * @param param.isReviewed - 是否已完成所有题目的批阅
 * @param param.setUserAnswers - 设置用户作答答案
 * @param param.hiddenAnswer - 隐藏标准答案
 * @param param.allowSave - 是否允许用户在不提交的情况下保存填写的答案
 * @param param.onSave - 保存答案回调
 * @returns React.ReactNode
 */
export const TestPaper: React.FC<{
  loading: boolean
  mode: QuizMode
  showScore: boolean
  blocks?: PaperBlock[]
  initValue?: PaperAnswer[] | null
  reviews?: PaperReview[]
  isForeground?: boolean
  onSubmit?: (answers: PaperAnswerReq[]) => void
  isReviewed?: boolean
  setUserAnswers?: (v: PaperAnswerReq[]) => void
  hiddenAnswer?: boolean
  allowSave?: boolean
  onSave?: (answers: PaperAnswerReq[]) => void
}> = ({
  blocks,
  loading,
  mode,
  initValue,
  reviews,
  onSubmit,
  isReviewed,
  setUserAnswers,
  hiddenAnswer,
  allowSave,
  onSave,
  showScore,
}) => {
  const [answers, setAnswers] = useState<PaperAnswerReq[]>([])
  const { api: createReview } = paperApi.useCreateReview({
    reload: [
      quizApi.queryReflectionCompleted,
      courseApi.queryCourseReflectionCompleted,
    ],
  })

  useEffect(() => {
    const finalAnswers = answers.filter(
      (answer) => answer.value && answer.value.length
    )
    setUserAnswers && setUserAnswers([...finalAnswers])
  }, [answers])

  useEffect(() => {
    // 作答模式下不显示之前作答的答案（抽题模式）
    if (mode !== QuizMode.Answering || allowSave) {
      setAnswers(initValue || [])
    } else {
      setAnswers([])
    }
  }, [initValue, mode])

  const onCreateReview = async (
    params: CreateReviewParams,
    payload: CreateReviewPayload
  ) => {
    await createReview({
      bizId: 0,
      bizType: BizTypeEnum.Quiz,
      activityTaskId: 0,
      ...params,
      payload,
    })
  }
  const finalBlocks = AddSerialNumberForQuestionBlock(blocks || [])
  if (mode === QuizMode.Empty) return null
  if (loading) return <Skeleton active />
  if (!blocks) return null
  return (
    <AnswersContext.Provider
      value={{
        mode,
        answers,
        setAnswers: (v: PaperAnswerReq[]) => setAnswers(v),
        reviews: reviews || [],
        onCreateReview,
        isReviewed: !!isReviewed,
        blocks: blocks || [],
        showAnswer: !hiddenAnswer,
        showScore,
      }}
    >
      <Wrapper>
        {finalBlocks.map((block) => {
          return (
            <div
              id={`${block.serialNumber || block.id}`}
              key={block.id}
            >
              <Block
                key={block.id}
                block={block}
              />
            </div>
          )
        })}
        {[QuizMode.Answering].includes(mode) ? (
          allowSave ? (
            <AllowSaveButtonWrapper>
              <div>
                <Button
                  id="test-paper-save"
                  onClick={() => {
                    const finalAnswers = answers.filter(
                      (answer) => answer.value && !!answer.value.length
                    )
                    onSave && onSave(finalAnswers)
                  }}
                >
                  保存
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    const finalAnswers = answers.filter(
                      (answer) => answer.value && !!answer.value.length
                    )
                    onSubmit && onSubmit(finalAnswers)
                  }}
                >
                  提交
                </Button>
              </div>
            </AllowSaveButtonWrapper>
          ) : (
            <ButtonWrapper>
              <Button
                type="primary"
                onClick={async () => {
                  if ([QuizMode.Preview].includes(mode)) {
                    Toast.warning('预览模式下不支持数据提交')
                    return
                  }
                  const finalAnswers = answers.filter(
                    (answer) => answer.value && answer.value.length
                  )
                  onSubmit && onSubmit(finalAnswers)
                }}
              >
                提交
              </Button>
            </ButtonWrapper>
          )
        ) : null}
      </Wrapper>
    </AnswersContext.Provider>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 12px;
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  &&& {
    .ant-btn-primary {
      width: 120px;
      height: 40px;
    }
  }
`

const AllowSaveButtonWrapper = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  &&& {
    .ant-btn-default,
    .ant-btn-primary {
      width: 120px;
      height: 40px;
      margin-right: 12px;
    }
  }
`
